import { useMemo } from "react";

import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { EMPTY_VALUE_PLACEHOLDER } from "@shared";
import { format } from "date-fns";

import { CaseNoteLink } from "@/common/components/case-note-link/CaseNoteLink";
import { ClientLink } from "@/common/components/client-link/ClientLink";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { EdictRow } from "@/pages/edicts/Edicts.types";
import { ClientBadge } from "@/pages/edicts/components/client-badge/ClientBadge";
import { useTranslation } from "@/translations";

export function useEdictsColumns(showCityColumn: boolean): Array<GridColDef<EdictRow>> {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  return useMemo<Array<GridColDef<EdictRow>>>(
    () => [
      {
        field: "caseNumber",
        headerName: t("edicts.columns.id"),
        maxWidth: 140,
        flex: 0.7,
        renderCell: (params: GridRenderCellParams<EdictRow>) => {
          const { clientId, caseNumber } = params.row;
          if (clientId === null || caseNumber === null) {
            return EMPTY_VALUE_PLACEHOLDER;
          }
          return <ClientLink clientId={clientId} name={caseNumber} variant="body2" />;
        },
      },
      {
        field: "debtorName",
        headerName: t("edicts.columns.debtor"),
        flex: 1,
        renderCell: (params: GridRenderCellParams<EdictRow>) => (
          <EllipsisText value={params.row.debtorName} variant="body2" fontWeight={600} />
        ),
      },
      {
        field: "birthday",
        headerName: t("edicts.columns.birthday"),
        maxWidth: 180,
        flex: 0.7,
        renderCell: (params: GridRenderCellParams<EdictRow>) =>
          params.row.birthday !== null
            ? format(params.row.birthday, "P", { locale: locales[language] })
            : EMPTY_VALUE_PLACEHOLDER,
      },
      ...(showCityColumn
        ? [
            {
              field: "city",
              headerName: t("edicts.columns.city"),
              maxWidth: 180,
              flex: 0.7,
              renderCell: (params: GridRenderCellParams<EdictRow>) => params.row.city ?? EMPTY_VALUE_PLACEHOLDER,
            },
          ]
        : []),
      {
        field: "counselorUserCode",
        headerName: t("edicts.columns.counselor"),
        maxWidth: 180,
        flex: 0.6,
        renderCell: (params: GridRenderCellParams<EdictRow>) => {
          // Only a matched client can have a counselor, so an unmatched row says nothing rather than
          // claiming nobody is assigned.
          if (params.row.clientId === null) {
            return EMPTY_VALUE_PLACEHOLDER;
          }
          return params.row.counselorUserCode ?? t("edicts.notAssigned");
        },
      },
      {
        field: "caseNoteId",
        headerName: t("edicts.columns.recorded"),
        maxWidth: 180,
        flex: 0.7,
        sortable: false,
        renderCell: (params: GridRenderCellParams<EdictRow>) => {
          const { clientId, caseNoteId } = params.row;
          if (clientId === null || caseNoteId === null) {
            return null;
          }
          return (
            <CaseNoteLink
              clientId={clientId}
              caseNoteId={caseNoteId}
              label={t("edicts.columns.recordedLink")}
              variant="body2"
            />
          );
        },
      },
      {
        field: "clientId",
        headerName: t("edicts.columns.client"),
        maxWidth: 180,
        flex: 0.8,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: (params: GridRenderCellParams<EdictRow>) => <ClientBadge isClient={params.row.clientId !== null} />,
      },
    ],
    [t, language, showCityColumn]
  );
}
